import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { TicketSummary, TicketDetail } from "@shared/types";
import { fetchTickets, fetchTicketDetail } from "../../lib/api";
import { Markdown } from "./Markdown";

/**
 * 我的工单 tracking panel (§20.6). A modal that lists the enterprise's support
 * tickets with status, in two modes: all company tickets (default) or only the
 * current user's (filtered by a remembered email). The server proxies GitHub,
 * so the user tracks status in-app without any GitHub access or login.
 */

const EMAIL_KEY = "cyclecare_email";

interface TicketsPanelProps {
  onClose: () => void;
  /** Caller's role. end_users are confined to "我的工单" (#94): the company-wide
   * tab is hidden and the view defaults to their own email-scoped tickets. */
  role?: string | null;
}

function formatDate(iso: string): string {
  if (iso.length === 0) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}月${d.getDate()}日`;
}

export function TicketsPanel({ onClose, role }: TicketsPanelProps): JSX.Element {
  // end_users may only see their own tickets, so the company-wide tab is hidden
  // and the view starts in "mine" (#94). agent/admin keep the company-wide view.
  const canSeeAll = role !== "end_user";
  const [mode, setMode] = useState<"all" | "mine">(canSeeAll ? "all" : "mine");
  // end_user starts unfiltered (full account-scoped list); only the self-service
  // agent/admin view restores a remembered lookup email.
  const [email, setEmail] = useState<string>(() =>
    canSeeAll ? localStorage.getItem(EMAIL_KEY) ?? "" : ""
  );
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [enterprise, setEnterprise] = useState<string>("");
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape (modal a11y).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Move focus into the dialog when it (or the sub-view) mounts.
  useEffect(() => {
    modalRef.current?.focus();
  }, [detail, detailLoading]);

  const openDetail = useCallback(async (num: number): Promise<void> => {
    setDetailLoading(true);
    setDetail(null);
    // Ownership is enforced server-side from the auth token for end_users (#94).
    const d = await fetchTicketDetail(num);
    setDetail(d);
    setDetailLoading(false);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const useEmail = mode === "mine" ? email.trim() : undefined;
    const res = await fetchTickets(useEmail);
    setTickets(res.tickets);
    setEnterprise(res.enterprise);
    setConfigured(res.configured);
    setLoading(false);
  }, [mode, email]);

  useEffect(() => {
    // For an end_user the list is account-scoped server-side, so it loads with
    // or without an email (the email is only an optional within-account filter).
    // For agent/admin the self-service "mine" view still needs an email first.
    if (canSeeAll && mode === "mine" && email.trim().length === 0) {
      setTickets([]);
      return;
    }
    void load();
  }, [mode, load, email, canSeeAll]);

  const onApplyEmail = (): void => {
    localStorage.setItem(EMAIL_KEY, email.trim());
    void load();
  };

  // Ticket detail sub-view (clicked a row): description + human follow-up.
  if (detailLoading || detail !== null) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div
          className="modal tickets-modal"
          role="dialog"
          aria-modal="true"
          aria-label="工单详情"
          tabIndex={-1}
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-head">
            <button className="ticket-back" type="button" onClick={() => setDetail(null)}>
              ‹ 返回
            </button>
            <button className="rail-close" type="button" aria-label="关闭" onClick={onClose}>
              ×
            </button>
          </div>
          {detailLoading || detail === null ? (
            <div className="tickets-empty">加载中…</div>
          ) : (
            <div className="ticket-detail">
              <div className="ticket-detail-head">
                <span className={`ticket-state ${detail.state === "closed" ? "closed" : "open"}`}>
                  {detail.state === "closed" ? "已解决" : "处理中"}
                </span>
                <span className="ticket-detail-title">{detail.title.replace(/^\[工单\]\s*/, "")}</span>
              </div>
              <div className="ticket-detail-meta">
                #{detail.number} · {formatDate(detail.createdAt)}
              </div>
              <div className="ticket-detail-body">
                <Markdown>{detail.body}</Markdown>
              </div>
              <div className="ticket-detail-comments">
                <div className="ticket-detail-comments-label">
                  客服跟进 {detail.comments.length > 0 ? `(${detail.comments.length})` : ""}
                </div>
                {detail.comments.length === 0 ? (
                  <div className="tickets-empty">暂无客服回复，处理后会在此显示。</div>
                ) : (
                  detail.comments.map((c, i) => (
                    <div key={i} className="ticket-comment">
                      <div className="ticket-comment-meta">
                        {c.author} · {formatDate(c.createdAt)}
                      </div>
                      <div className="ticket-comment-body">{c.body}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal tickets-modal"
        role="dialog"
        aria-modal="true"
        aria-label="工单列表"
        tabIndex={-1}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div className="modal-title">工单 · {enterprise}</div>
          <button className="rail-close" type="button" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </div>

        {canSeeAll && (
          <div className="seg">
            <button
              className={`seg-btn ${mode === "all" ? "active" : ""}`}
              type="button"
              onClick={() => setMode("all")}
            >
              公司全部工单
            </button>
            <button
              className={`seg-btn ${mode === "mine" ? "active" : ""}`}
              type="button"
              onClick={() => setMode("mine")}
            >
              我的工单
            </button>
          </div>
        )}

        {mode === "mine" && (
          <div className="tickets-email">
            <input
              className="composer-input"
              type="email"
              placeholder={canSeeAll ? "输入您提交工单时填写的邮箱" : "可选：按邮箱筛选您的工单"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onApplyEmail();
              }}
            />
            <button className="send-btn" type="button" onClick={onApplyEmail}>
              {canSeeAll ? "查询" : "筛选"}
            </button>
          </div>
        )}

        <div className="tickets-list">
          {!configured ? (
            <div className="tickets-empty">工单后端未配置。</div>
          ) : loading ? (
            <div className="tickets-empty">加载中…</div>
          ) : tickets.length === 0 ? (
            <div className="tickets-empty">
              {!canSeeAll
                ? email.trim().length > 0
                  ? "该邮箱下暂无工单。"
                  : "您还没有提交过工单。"
                : mode === "mine"
                  ? "该邮箱下暂无工单。"
                  : "暂无工单。"}
            </div>
          ) : (
            tickets.map((t) => (
              <button
                key={t.number}
                type="button"
                className="ticket-row"
                onClick={() => void openDetail(t.number)}
              >
                <span className={`ticket-state ${t.state === "closed" ? "closed" : "open"}`}>
                  {t.state === "closed" ? "已解决" : "处理中"}
                </span>
                <span className="ticket-title">{t.title.replace(/^\[工单\]\s*/, "")}</span>
                <span className="ticket-meta">
                  #{t.number} · {formatDate(t.createdAt)}
                  {canSeeAll && t.tenant !== undefined && t.tenant.length > 0 ? ` · 客户 ${t.tenant}` : ""}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
